// @ts-nocheck
/* MTN Haus — interactive bits.
   Everything uses event delegation on document so it survives Shopify
   theme-customizer re-renders (when sections are added / reordered without
   saving, Shopify replaces section HTML and any direct listeners on the old
   DOM nodes are lost). */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Order matters for main-thread cost: every DOM write runs first, and no
  // init reads layout synchronously (ResizeObserver / rAF instead). Mixing
  // writes and getBoundingClientRect here forced several full-page layouts
  // on load.
  ready(function () {
    initTestScroller();
    initTrustedV2();
    initCaseIndexes();
    initCountUp();
    initHeroWordSwap();
    initLazyVideos();

    initAnchorSmoothScroll();
    initScrollToTop();
    initServicesPills();
    initBudgetPills();
    initMobileBurger();
    initContactSubmitBusyState();
    initNavDropdowns();
    initNewsletterLoadingState();

    initHeaderHeight();
    initAutohideOnScroll();
    initProcessCursor();
    initHeroParallax();
    focusServerErrorSummary();
  });

  // Process section — single floating orange cursor that overlays the active
  // step's grey dot. Translates X+Y so it follows the active step across grid
  // wraps (mobile 2-col). Desktop follows hover; mobile follows click.
  // Multiple rails supported (one per Shopify section).
  // Ported from mtn-v2.js (WP).
  function initProcessCursor() {
    var rails = document.querySelectorAll('.js-proc-rail');
    if (!rails.length) return;
    var mqDesktop = window.matchMedia('(min-width: 750px)');

    rails.forEach(function (rail) {
      if (rail.dataset.cursorInit === '1') return;
      rail.dataset.cursorInit = '1';

      var cursor = rail.querySelector('.js-proc-cursor');
      var steps = rail.querySelectorAll('.proc-step');
      if (!cursor || !steps.length) return;

      // Initial active step priority:
      //   1. an explicit `.is-active` in the DOM,
      //   2. the block marked as the first step (`.proc-step--first` set via
      //      the `first_step` block setting),
      //   3. fallback to the first step in DOM order.
      var activeStep = rail.querySelector('.proc-step.is-active') ||
                       rail.querySelector('.proc-step--first') ||
                       steps[0];
      activeStep.classList.add('is-active');

      var positionCursor = function (step) {
        var railRect = rail.getBoundingClientRect();
        var stepRect = step.getBoundingClientRect();
        var dot = step.querySelector('.proc-step__dot');
        var dotOffsetX = dot ? dot.offsetLeft : 24;
        var x = stepRect.left - railRect.left + dotOffsetX;
        var y = stepRect.top - railRect.top;
        cursor.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      };

      var setActive = function (step) {
        if (step === activeStep) return;
        activeStep.classList.remove('is-active');
        activeStep = step;
        activeStep.classList.add('is-active');
        positionCursor(step);
      };

      steps.forEach(function (step) {
        step.addEventListener('mouseenter', function () {
          if (mqDesktop.matches) setActive(step);
        });
        step.addEventListener('focusin', function () { setActive(step); });
        step.addEventListener('click', function () { setActive(step); });
      });

      // ResizeObserver fires after layout, so the rect reads below are free.
      // It also covers viewport resizes and font-load reflows. The first
      // placement skips the transition; it is restored on the next frame.
      var placed = false;
      new ResizeObserver(function () {
        if (!placed) cursor.style.transition = 'none';
        positionCursor(activeStep);
        if (!placed) {
          placed = true;
          requestAnimationFrame(function () { cursor.style.transition = ''; });
        }
      }).observe(rail);
    });
  }

  // Count-up animation for stat numbers. Fires once when the element enters
  // the viewport. Parses prefix + number + decimals from the source text so
  // "$1", "80", "9.5" all work — animates 0 → target preserving decimals.
  // Ported from mtn-v2.js (WP).
  function initCountUp() {
    var nums = document.querySelectorAll('.js-count-up');
    if (!nums.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var parse = function (text) {
      var m = String(text).trim().match(/^([^\d.\-]*)(-?\d+(?:\.\d+)?)([^\d]*)$/);
      if (!m) return null;
      var prefix = m[1] || '';
      var raw = m[2];
      var target = parseFloat(raw);
      if (!isFinite(target)) return null;
      var dot = raw.indexOf('.');
      var decimals = dot >= 0 ? raw.length - dot - 1 : 0;
      var suffix = m[3] || '';
      return { prefix: prefix, target: target, decimals: decimals, suffix: suffix };
    };

    var animate = function (el) {
      var parsed = parse(el.dataset.original || el.textContent);
      if (!parsed) return;
      if (!el.dataset.original) el.dataset.original = el.textContent;
      var prefix = parsed.prefix, target = parsed.target, decimals = parsed.decimals, suffix = parsed.suffix;
      var dur = 1400;
      var start = performance.now();
      var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
      var tick = function (now) {
        var t = Math.min(1, (now - start) / dur);
        var v = ease(t) * target;
        var display = decimals > 0 ? v.toFixed(decimals) : Math.round(v).toString();
        el.textContent = prefix + display + suffix;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    nums.forEach(function (el) {
      if (el.dataset.countInit === '1') return;
      el.dataset.countInit = '1';
      var parsed = parse(el.textContent);
      if (!parsed) return;
      el.dataset.original = el.textContent;
      var zero = parsed.decimals > 0 ? (0).toFixed(parsed.decimals) : '0';
      el.textContent = parsed.prefix + zero + parsed.suffix;
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          animate(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });

    nums.forEach(function (n) { io.observe(n); });
  }

  // Hero background parallax. Opt-in via data-parallax="1" on .js-hero-bg.
  // Ported from mtn-v2.js (WP).
  function initHeroParallax() {
    var bg = document.querySelector('.js-hero-bg[data-parallax]');
    if (!bg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var raf = null;
    var onScroll = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var y = Math.max(0, window.scrollY) * 0.35;
        bg.style.transform = 'translate3d(0,' + y + 'px,0) scale(1.08)';
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Hero heading word-swap — typewriter cycle through phrases stored in
  // data-words (JSON array) on any .js-hero-swap element. Multiple instances
  // supported (one per section). Honours prefers-reduced-motion.
  function initHeroWordSwap() {
    var targets = document.querySelectorAll('.js-hero-swap');
    if (!targets.length) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    targets.forEach(function (target) {
      if (target.dataset.swapInit === '1') return;
      target.dataset.swapInit = '1';

      var words;
      try { words = JSON.parse(target.dataset.words || '[]'); }
      catch (e) { return; }
      if (!Array.isArray(words) || words.length === 0) return;

      var speed = Math.max(10, parseInt(target.dataset.speed, 10) || 38);
      var pause = Math.max(200, parseInt(target.dataset.pause, 10) || 2600);

      if (reduced) {
        target.textContent = words[0];
        return;
      }

      // Every typed character is a DOM write, so the loop holds its next step
      // while the headline is off screen and resumes from it on return.
      var onScreen = true;
      var pending = null;
      var schedule = function (fn, ms) {
        setTimeout(function () {
          if (onScreen) fn();
          else pending = fn;
        }, ms);
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen && pending) {
            var next = pending;
            pending = null;
            next();
          }
        }).observe(target);
      }

      var i = 0;
      var typeWord = function (word) {
        target.textContent = '';
        var n = 0;
        var tick = function () {
          if (n <= word.length) {
            target.textContent = word.slice(0, n);
            n++;
            schedule(tick, speed);
          } else {
            schedule(function () {
              i = (i + 1) % words.length;
              typeWord(words[i]);
            }, pause);
          }
        };
        tick();
      };

      // First word is already rendered server-side; pause then cycle.
      schedule(function () { typeWord(words[(++i) % words.length]); }, 3000);
    });
  }

  // Auto-number .s-case__index spans, grouped by their shopify section ancestor.
  // Runs on load and re-runs on Shopify section:load (customizer re-renders).
  function initCaseIndexes() {
    var groups = new Map();
    document.querySelectorAll('.s-case').forEach(function (card) {
      var section = card.closest('[id^="shopify-section-"]');
      if (!section) return;
      if (!groups.has(section.id)) groups.set(section.id, []);
      groups.get(section.id).push(card);
    });
    groups.forEach(function (cards) {
      var total = String(cards.length).padStart(2, '0');
      cards.forEach(function (card, i) {
        var el = card.querySelector('.s-case__index');
        if (el) el.textContent = String(i + 1).padStart(2, '0') + ' / ' + total;
      });
    });
  }

  // Background videos (case cards) carry their URL in data-src. Autoplay
  // ignores preload, so a src in the markup downloaded every case video on
  // page load. Attach the source when the video nears the viewport, and pause
  // it while it is off screen.
  var lazyVideoObserver = null;
  function initLazyVideos() {
    var videos = document.querySelectorAll('video.js-lazy-video[data-src]');
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach(function (v) { v.src = v.dataset.src; v.play().catch(function () {}); });
      return;
    }
    if (!lazyVideoObserver) {
      lazyVideoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          if (e.isIntersecting) {
            if (!v.getAttribute('src')) v.src = v.dataset.src;
            v.play().catch(function () {});
          } else if (v.getAttribute('src')) {
            v.pause();
          }
        });
      }, { rootMargin: '200px 0px' });
    }
    videos.forEach(function (v) { lazyVideoObserver.observe(v); });
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('video.js-lazy-video')) initLazyVideos();
    if (e.target.querySelector('.s-case')) initCaseIndexes();
    if (e.target.querySelector('.js-hero-bg[data-parallax]')) initHeroParallax();
    if (e.target.querySelector('.js-hero-swap')) initHeroWordSwap();
    if (e.target.querySelector('.js-count-up')) initCountUp();
    if (e.target.querySelector('.js-proc-rail')) initProcessCursor();
    if (e.target.querySelector('.js-test-root')) initTestScroller();
    if (e.target.querySelector('.js-trusted-root')) initTrustedV2();
  });

  // Trusted v2 — wraps `.trusted-card` blocks into a scroller + row, injects
  // controls (counter + prev/next arrows) as a sibling of the header group,
  // and keeps the counter in sync with the scroll position.
  function initTrustedV2() {
    var roots = document.querySelectorAll('.js-trusted-root');
    roots.forEach(function (root) {
      if (root.dataset.trustedReady === '1') return;
      var cards = root.querySelectorAll(':scope > .trusted-card');
      if (!cards.length) return;

      var section = root.closest('.trusted-v2');
      var labelPrev = (section && section.dataset.labelPrev) || 'Previous';
      var labelNext = (section && section.dataset.labelNext) || 'Next';
      var total = cards.length;
      var totalStr = String(total).padStart(2, '0');

      // Controls: counter + prev + next.
      var controls = document.createElement('div');
      controls.className = 'trusted-v2__controls';
      controls.innerHTML =
        '<span class="trusted-v2__counter js-trusted-counter">01 / ' + totalStr + '</span>' +
        '<button type="button" class="trusted-v2__arrow js-trusted-prev" aria-label="' + labelPrev + '">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>' +
        '</button>' +
        '<button type="button" class="trusted-v2__arrow js-trusted-next" aria-label="' + labelNext + '">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>' +
        '</button>';

      // Scroller wrap + scroller + row.
      var wrap = document.createElement('div');
      wrap.className = 'trusted-v2__scroller-wrap';
      var scroller = document.createElement('div');
      scroller.className = 'trusted-v2__scroller js-trusted-scroller';
      var row = document.createElement('div');
      row.className = 'trusted-v2__row';
      scroller.appendChild(row);
      wrap.appendChild(scroller);

      // Insert controls + wrap before the first card, then move cards into row.
      root.insertBefore(controls, cards[0]);
      root.insertBefore(wrap, cards[0]);
      cards.forEach(function (card) { row.appendChild(card); });

      // Counter sync — uses the first card's offset width + row gap.
      var counter = controls.querySelector('.js-trusted-counter');
      function updateCounter() {
        var first = row.querySelector('.trusted-card');
        if (!first) return;
        var gap = parseFloat(getComputedStyle(row).columnGap || '16') || 16;
        var step = first.offsetWidth + gap;
        if (step <= 0) return;
        var idx = Math.min(total, Math.max(1, Math.round(scroller.scrollLeft / step) + 1));
        counter.textContent = String(idx).padStart(2, '0') + ' / ' + totalStr;
      }
      scroller.addEventListener('scroll', updateCounter, { passive: true });

      root.dataset.trustedReady = '1';
    });
  }

  // In The Spotlight — this section's slider is now a Swiper instance owned
  // by the `<spotlight-swiper>` Web Component (assets/spotlight-swiper.js),
  // not vanilla scroll-snap. See that file for the deliberate Tier 1
  // exception (Swiper) approved for this section specifically.

  // Snap-aware one-card scroll for the trusted/test sliders.
  //
  // These scrollers use `scroll-snap-type: x mandatory` plus trailing row
  // padding (and a trailing `::after` spacer on mobile), so the logical
  // `scrollWidth - clientWidth` overshoots the LAST card's snap position. A
  // plain `scrollBy`/clamp-to-scrollWidth therefore still nudges past the end
  // on iOS; mandatory snap then yanks it back — the "it scrolls and returns
  // the last slide" bounce. Detecting the snapped card and stepping by index
  // (with an early return at the boundary) makes "next" a true no-op at the
  // end and "prev" a true no-op at the start, immune to overscroll.
  function snapNavCards(scroller, cards, dir) {
    if (!scroller || !cards.length) return;
    var scLeft = scroller.getBoundingClientRect().left;
    var scrollLeft = scroller.scrollLeft;
    // Each card's left position in the scroller's content coordinates
    // (independent of the current scroll offset).
    function pos(card) {
      return card.getBoundingClientRect().left - scLeft + scrollLeft;
    }
    // Use the scroller's own `scroll-padding-inline-start` as the leading
    // gutter, so our targets land exactly on the browser's snap points (no
    // re-snap, no fighting the gutter). The CSS owns the value — 20px on
    // mobile, 0 on desktop — and reading it keeps both breakpoints consistent.
    var lead = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
    var max = scroller.scrollWidth - scroller.clientWidth;
    // Each card's REACHABLE scroll target. Trailing cards whose snap point lies
    // past `max` clamp to it (e.g. 2 wide cards on desktop: the last one can
    // only scroll to `max`, never to its own start). Matching against the
    // clamped targets — not the raw card positions — keeps "prev" working
    // from that clamped end position.
    var targets = [];
    for (var i = 0; i < cards.length; i++) {
      targets.push(Math.max(0, Math.min(max, pos(cards[i]) - lead)));
    }
    var current = 0;
    var best = Infinity;
    for (var j = 0; j < targets.length; j++) {
      var d = Math.abs(targets[j] - scrollLeft);
      if (d < best) { best = d; current = j; }
    }
    // Step to the next card whose target actually moves the scroller; cards
    // sharing a clamped target (all at 0 or all at `max`) are skipped.
    var next = current + dir;
    while (next >= 0 && next < targets.length && Math.abs(targets[next] - scrollLeft) < 1) {
      next += dir;
    }
    if (next < 0 || next >= targets.length) return; // at a boundary — do nothing
    scroller.scrollTo({ left: targets[next], behavior: 'smooth' });
  }

  // Delegated nav for Trusted v2 — advances one card, snap-aware.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.trusted-v2 .js-trusted-prev, .trusted-v2 .js-trusted-next');
    if (!btn) return;
    var root = btn.closest('.js-trusted-root');
    if (!root) return;
    var scroller = root.querySelector('.js-trusted-scroller');
    var row = root.querySelector('.trusted-v2__row');
    if (!scroller || !row) return;
    var dir = btn.classList.contains('js-trusted-prev') ? -1 : 1;
    snapNavCards(scroller, row.querySelectorAll('.trusted-card'), dir);
  });

  // Testimonials v2 — wraps `.test-card` blocks into a horizontal snap
  // scroller and injects the prev/next arrow controls. Section markup
  // emits cards as flat siblings (because `content_for 'blocks'` cannot
  // be subdivided in Liquid); this hoists them into the WP `.s-test`
  // structure at runtime.
  function initTestScroller() {
    var roots = document.querySelectorAll('.js-test-root');
    roots.forEach(function (root) {
      if (root.dataset.testReady === '1') return;
      var cards = root.querySelectorAll(':scope > .test-card');
      if (!cards.length) return;

      var section = root.closest('.test-v2');
      var labelPrev = (section && section.dataset.testControlsLabelPrev) || 'Previous testimonial';
      var labelNext = (section && section.dataset.testControlsLabelNext) || 'Next testimonial';

      var controls = document.createElement('div');
      controls.className = 'test-v2__controls';
      controls.innerHTML =
        '<button type="button" class="test-v2__arrow js-test-prev" aria-label="' + labelPrev + '">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>' +
        '</button>' +
        '<button type="button" class="test-v2__arrow js-test-next" aria-label="' + labelNext + '">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>' +
        '</button>';

      var scroller = document.createElement('div');
      scroller.className = 'test-v2__scroller js-test-scroller';

      // Insert controls + scroller before the first card, then move all
      // cards into the scroller in their original order.
      root.insertBefore(controls, cards[0]);
      root.insertBefore(scroller, cards[0]);
      cards.forEach(function (card) { scroller.appendChild(card); });

      root.dataset.testReady = '1';
    });
  }

  // Delegated nav for testimonial scrollers.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-test-prev, .js-test-next');
    if (!btn) return;
    var root = btn.closest('.js-test-root');
    if (!root) return;
    var scroller = root.querySelector('.js-test-scroller');
    if (!scroller) return;
    var dir = btn.classList.contains('js-test-prev') ? -1 : 1;
    // Snap-aware, same boundary-safe nav as trusted-v2 (see snapNavCards).
    snapNavCards(scroller, scroller.querySelectorAll('.test-card'), dir);
  });

  // Footer newsletter — show loading state on the button during the brief
  // window between submit and the page reload. We do NOT preventDefault here:
  // the native browser submit must run so that Shopify's invisible CAPTCHA
  // token gets injected. Without that token Shopify returns 400.
  function initNewsletterLoadingState() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.nodeName !== 'FORM') return;
      if (!form.classList || !form.classList.contains('mtn-footer__news')) return;

      form.classList.add('is-loading');
      var button = form.querySelector('.mtn-footer__news-go');
      if (button) {
        // Keep submission behavior intact (no `disabled`, no textContent
        // replacement — that would cancel the POST or wipe the value Shopify
        // captures). `data-busy` triggers the CSS spinner inside the button.
        button.dataset.busy = '1';
      }
    });
  }

  // After a server-side validation error, scroll the error summary into view
  // and focus it so screen readers announce it.
  function focusServerErrorSummary() {
    var summary = document.querySelector('.s-contact-form__error-summary');
    if (!summary) return;
    summary.scrollIntoView({ block: 'center', behavior: 'smooth' });
    summary.focus({ preventScroll: true });
  }


  // Footer wordmark — click scrolls the page back to the very top.
  function initScrollToTop() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js-mtn-to-top'); 
      if (!btn) return;

      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // 1) Smooth scroll for in-page anchors. Offsets by MTN header height.
  function initAnchorSmoothScroll() {
    document.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      var link = e.target.closest('a[href*="#"]');
      if (!link || link.target === '_blank') return;

      var url;
      try { url = new URL(link.href, window.location.href); } catch (_) { return; }
      if (url.pathname !== window.location.pathname || !url.hash || url.hash === '#') return;

      var target;
      try { target = document.querySelector(url.hash); } catch (_) { return; }
      if (!target) return;

      e.preventDefault();

      var nav = document.querySelector('.mtn-nav') || document.querySelector('header-component') || document.querySelector('.header');
      var navHeight = nav ? nav.getBoundingClientRect().height : 0;
      var top = target.getBoundingClientRect().top + window.scrollY - navHeight - 8;

      window.scrollTo({ top: top, behavior: 'smooth' });
      if (history.pushState) history.pushState(null, '', url.hash);

      // Close mobile drawer if open.
      var mtnNav = document.querySelector('.js-mtn-nav');
      if (mtnNav && mtnNav.classList.contains('is-open')) {
        setMobileNavOpen(false);
      }
    });
  }


  // 2b) Services index — intro filter pills: exclusive .is-on toggle on click.
  // Scrolling itself is handled by initAnchorSmoothScroll (pills are anchors).
  function initServicesPills() {
    document.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('.s-svc-intro__pill');
      if (!pill) return;
      var wrap = pill.closest('.s-svc-intro__pills');
      if (!wrap) return;
      wrap.querySelectorAll('.s-svc-intro__pill.is-on').forEach(function (p) {
        p.classList.remove('is-on');
      });
      pill.classList.add('is-on');
    });
  }


  // 3) Budget pill group inside the contact form — single-select, sets hidden input.
  function initBudgetPills() {
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.s-contact-form__pill');
      if (!pill) return;
      e.preventDefault();

      var group = pill.closest('.js-budget');
      if (!group) return;

      var pills = group.querySelectorAll('.s-contact-form__pill');
      pills.forEach(function (p) { p.classList.remove('is-on'); });
      pill.classList.add('is-on');

      var hidden = group.querySelector('input[type="hidden"]');
      if (hidden) {
        hidden.value = pill.dataset.value || pill.textContent.trim();
        hidden.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  // Sync `--mtn-nav-h` to the real header height so non-overlay pages get
  // `main#MainContent { padding-top: var(--mtn-nav-h) }` clearance that
  // tracks any change in the header's actual rendered size (logo swap,
  // setting tweak in the editor, viewport resize, font load).
  // A ResizeObserver reports after layout and before paint, so this never
  // forces a synchronous layout and still lands before the first frame.
  function initHeaderHeight() {
    var header = document.querySelector('.mtn-nav');
    if (!header) return;
    var ticker = document.querySelector('.promo-ticker');
    function sync() {
      var navH = header.getBoundingClientRect().height;
      var tickerH = ticker ? ticker.getBoundingClientRect().height : 0;
      if (navH > 0) {
        // --mtn-nav-h is the total clearance the main content needs to
        // sit below: ticker (if any) + nav. The nav's own `top` reads
        // --mtn-ticker-h separately so it slides under the ticker.
        document.documentElement.style.setProperty('--mtn-nav-h', (navH + tickerH) + 'px');
        if (ticker) document.documentElement.style.setProperty('--mtn-ticker-h', tickerH + 'px');
      }
    }
    var observer = new ResizeObserver(sync);
    function observe() {
      observer.disconnect();
      observer.observe(header);
      if (ticker) observer.observe(ticker);
    }
    observe();
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target.querySelector('.mtn-nav, .promo-ticker')) {
        // Re-query both — the customizer swaps section markup in place.
        header = document.querySelector('.mtn-nav') || header;
        ticker = document.querySelector('.promo-ticker');
        observe();
      }
    });
  }

  // Nav dropdowns — desktop hover is CSS-only via :hover. This JS handles:
  //   - Click-to-toggle the parent link on touch / mobile (where :hover
  //     doesn't apply reliably and the user would otherwise navigate
  //     straight to the parent's URL on first tap)
  //   - aria-expanded sync on the toggle for assistive technology
  //   - Close on outside click / Escape key
  // Uses event delegation on `document` so it survives section re-renders
  // (Shopify Section Rendering API swaps section markup in place).
  function initNavDropdowns() {
    var nav = document.querySelector('.mtn-nav');

    function isMobileNav() {
      return window.matchMedia('(max-width: 991px)').matches;
    }

    // Reflect "any dropdown is open" state on the nav itself so CSS can
    // lock the nav visible (override .is-hidden) while a panel is open.
    function syncOpenState() {
      if (!nav) return;
      var hasOpen = document.querySelector('.mtn-nav__item--dd.is-open, .mtn-nav__item--dd:hover');
      nav.classList.toggle('has-open-dd', !!hasOpen);
    }

    function closeAll(except) {
      document.querySelectorAll('.mtn-nav__item--dd.is-open').forEach(function (el) {
        if (el === except) return;
        el.classList.remove('is-open');
        var t = el.querySelector('.mtn-nav__link--toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      syncOpenState();
    }

    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('.mtn-nav__link--toggle');
      if (toggle) {
        var dd = toggle.closest('.mtn-nav__item--dd');
        if (!dd) return;
        var isOpen = dd.classList.contains('is-open');
        // On mobile (≤991px) OR touch-only devices, tap toggles the
        // dropdown instead of navigating. Multiple panels can stay open
        // at once inside the drawer (matches WP accordion behaviour) —
        // tapping a different toggle does not close the others.
        // Desktop click still navigates to the parent URL.
        if (isMobileNav() || window.matchMedia('(hover: none)').matches) {
          e.preventDefault();
          if (isOpen) {
            dd.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
          } else {
            dd.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
          }
          syncOpenState();
        }
        return;
      }

      // Outside-click closes any open dropdowns.
      if (!e.target.closest('.mtn-nav__item--dd')) {
        closeAll();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.mtn-nav__item--dd.is-open').forEach(function (el) {
        el.classList.remove('is-open');
        var t = el.querySelector('.mtn-nav__link--toggle');
        if (t) { t.setAttribute('aria-expanded', 'false'); t.focus(); }
      });
      syncOpenState();
    });

    // Desktop hover: mirror aria-expanded + sync the nav's has-open-dd
    // class so .is-hidden cannot kick in while a panel is hovered. SKIP on
    // mobile drawer — there hover events on touch devices can fire after
    // a tap and prematurely collapse the accordion.
    document.addEventListener('mouseenter', function (e) {
      if (isMobileNav()) return;
      if (!e.target || !e.target.closest) return;
      var dd = e.target.closest && e.target.closest('.mtn-nav__item--dd');
      if (dd) {
        var t = dd.querySelector('.mtn-nav__link--toggle');
        if (t) t.setAttribute('aria-expanded', 'true');
        syncOpenState();
      }
    }, true);
    document.addEventListener('mouseleave', function (e) {
      if (isMobileNav()) return;
      if (!e.target || !e.target.closest) return;
      var dd = e.target.closest && e.target.closest('.mtn-nav__item--dd');
      if (dd) {
        var t = dd.querySelector('.mtn-nav__link--toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
        dd.classList.remove('is-open');
        syncOpenState();
      }
    }, true);
  }

  // Autohide nav + ticker on scroll-down, reveal on scroll-up.
  // Enabled by default; merchant can turn off via `nav_autohide` setting
  // on mtn-header. Threshold avoids hiding on small scrolls near the top.
  function initAutohideOnScroll() {
    var header = document.querySelector('.mtn-nav');
    if (!header) return;
    var ticker = document.querySelector('.promo-ticker');
    var section = header.closest('[data-autohide]');
    // Default on; merchant can opt out by setting data-autohide="0".
    if (section && section.dataset.autohide === '0') return;

    // Reading scrollY during init forces layout; take it on the next frame.
    var lastY = 0;
    requestAnimationFrame(function () { lastY = window.scrollY; });
    var hidden = false;
    var THRESHOLD = 80;       // wait until scrolled past this before hiding
    var MIN_DELTA = 6;        // ignore micro-scrolls (touch jitter)
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.scrollY;
      var delta = y - lastY;

      // Always show near the top.
      if (y < THRESHOLD) {
        if (hidden) {
          header.classList.remove('is-hidden');
          if (ticker) ticker.classList.remove('is-hidden');
          hidden = false;
        }
        lastY = y;
        return;
      }
      if (Math.abs(delta) < MIN_DELTA) return;

      // Never hide while a dropdown is open (or hovered) — yanking the
      // nav off-screen would leave the panel orphaned. Matches WP.
      if (header.classList.contains('has-open-dd')) {
        if (hidden) {
          header.classList.remove('is-hidden');
          if (ticker) ticker.classList.remove('is-hidden');
          hidden = false;
        }
        lastY = y;
        return;
      }

      if (delta > 0 && !hidden) {
        header.classList.add('is-hidden');
        if (ticker) ticker.classList.add('is-hidden');
        hidden = true;
      } else if (delta < 0 && hidden) {
        header.classList.remove('is-hidden');
        if (ticker) ticker.classList.remove('is-hidden');
        hidden = false;
      }
      lastY = y;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  // Contact form: client-side validation relies entirely on the browser's
  // native HTML5 mechanism (the `required` attribute on inputs). Shopify
  // handles email format / spam / CAPTCHA server-side and renders errors via
  // `form.errors` in the section's liquid. We still flip the submit button
  // into a loading state on successful submit so the user gets feedback
  // during the round-trip.
  function initContactSubmitBusyState() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('.mtn-contact-form');
      if (!form) return;
      var submitBtn = form.querySelector('.s-contact-form__submit');
      if (submitBtn) submitBtn.dataset.busy = '1';
    });
  }

  // 4) Mobile burger — toggle .mtn-nav .is-open + scroll lock.
  function initMobileBurger() {
    document.addEventListener('click', function (e) {
      var burger = e.target.closest('.js-mtn-nav-burger');
      if (burger) {
        var nav = burger.closest('.mtn-nav') || document.querySelector('.js-mtn-nav');
        if (!nav) return;
        setMobileNavOpen(!nav.classList.contains('is-open'));
        return;
      }
      // Tapping a nav link inside the open drawer closes it — UNLESS the
      // link is a dropdown toggle, in which case the dropdown should
      // accordion-expand instead of dismissing the whole drawer.
      var link = e.target.closest('.mtn-nav__link');
      if (link && !link.classList.contains('mtn-nav__link--toggle')) {
        var mtnNav = document.querySelector('.js-mtn-nav');
        if (mtnNav && mtnNav.classList.contains('is-open')) setMobileNavOpen(false);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var nav = document.querySelector('.js-mtn-nav');
      if (nav && nav.classList.contains('is-open')) setMobileNavOpen(false);
    });
  }

  function setMobileNavOpen(open) {
    var nav = document.querySelector('.js-mtn-nav');
    var burger = document.querySelector('.js-mtn-nav-burger');
    if (!nav) return;
    nav.classList.toggle('is-open', open);
    if (burger) burger.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('mtn-nav-open', open);
  }
})();
